import { ReactNode, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { authSlice } from 'src/redux/slices'

import { User } from 'firebase/auth'
import { observeAuthState } from 'src/firebase'
import { currentProviderPreferredMode } from '../redux/providers/selectors'
import { useMeLazyQuery } from './queries/users/useMeQuery/useMeLazyQuery'
import { useUserProvidersGetLazy } from './queries/users/useUserProvidersQuery/useUserProvidersGetLazyQuery'
import { signInReturnRemember } from './signInReturn'

const PROTECTED_ROUTES = ['admin', 'preferred-mode']

interface Props {
  children?: ReactNode
}

const AuthProvider = ({ children }: Props) => {
  const [getMe] = useMeLazyQuery()
  const [providersGet] = useUserProvidersGetLazy()
  const dispatch = useDispatch()
  const preferredMode = useSelector(currentProviderPreferredMode)
  const navigate = useNavigate()
  const location = useLocation()

  // Where the operator is *now*, read inside the callback and never named in a
  // dependency array. Both of these change while the console is open — the path
  // on every navigation — and `observeAuthState` fires the moment it is
  // subscribed. So a callback that changed identity with them tore the
  // subscription down and built it again on every route change, and once the
  // two boot queries' 2.5 minute caches had expired that meant re-running both
  // of them to move between two screens.
  const latest = useRef({
    pathname: location.pathname,
    search: location.search,
    preferredMode,
  })
  latest.current = {
    pathname: location.pathname,
    search: location.search,
    preferredMode,
  }

  // Whether this page has had a session. A visitor who arrives signed out is
  // opening a page they want once signed in; one whose session just ended
  // (signed out, or ended by the server) is not, and the next person to sign
  // in on this tab is not sent to it.
  const hadSession = useRef(false)

  const onAuthChange = useCallback(
    async (user: User | null) => {
      const {
        pathname,
        search,
        preferredMode: currentPreferredMode,
      } = latest.current

      // When user is not authenticated, set state and redirect if needed
      if (!user) {
        dispatch(
          authSlice.actions.setAuthState({
            isDeterminate: true,
            user: null,
            isAdmin: false,
          }),
        )

        // Only redirect to login if not already on a login-related page
        if (!pathname.includes('login')) {
          // Kept for after the sign-in: the link someone opened, the owner
          // key step a QR code handed to this phone among them.
          if (!hadSession.current) {
            signInReturnRemember(`${pathname}${search}`)
          }
          navigate('/login', { replace: true })
        }
        return
      }

      hadSession.current = true

      // User is authenticated - fetch user data. Nothing in the providers query
      // reads the user, so the two go out together: chaining them made boot pay
      // a second full round trip for an answer it did not need first.
      const [sqlUser, providersResult] = await Promise.all([
        getMe(),
        providersGet(),
      ])

      const providers = providersResult ?? []

      const isForbiddenRoute = PROTECTED_ROUTES.find((element) =>
        pathname.includes(element),
      )

      const isAdmin = sqlUser?.isAdmin ?? false

      dispatch(
        authSlice.actions.setAuthState({
          isDeterminate: true,
          user,
          isAdmin,
        }),
      )

      // Admin users can skip onboarding - they don't need to create a provider
      if (providers.length === 0 && isForbiddenRoute && !isAdmin) {
        navigate('/onboarding', { replace: true })
        return
      }

      // Admin users can also skip preferred-mode selection
      if (!currentPreferredMode && isForbiddenRoute && !isAdmin) {
        navigate('/preferred-mode', { replace: true })
        return
      }
    },
    [dispatch, getMe, providersGet, navigate],
  )

  // The same trick one level up. None of the four dependencies above is
  // contractually stable — `getMe` and `providersGet` are rebuilt whenever
  // Apollo hands back a new execute function — and one of them changing would
  // resubscribe, which is the cost this file just removed. The effect holds the
  // subscription for the life of the provider and reads the callback through a
  // ref, so there is exactly one subscription per session.
  const onAuthChangeRef = useRef(onAuthChange)
  onAuthChangeRef.current = onAuthChange

  useEffect(() => observeAuthState((user) => onAuthChangeRef.current(user)), [])

  return <>{children}</>
}

export default AuthProvider
